import Vue, { CreateElement } from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import ClickOutside from 'vue-click-outside'
import async from 'async'

import { HealthGoal, HealthIndicator, HealthIndicatorType } from '@src/data/goal'
import { Body2, H6, Subtitle1 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Input } from '@src/elements/input/input'
import ApiClient from '@src/lib/api_client'
import Activity from '@src/data/activity'
import { snakecaseKeys } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import { validateHealthIndicator } from '@src/util/validation'
import { Modal } from '@src/elements/modal/modal'
import { Button } from '@src/elements/button/button'
import { KGITooltip } from '@src/routes/members/components/health-goals/kgi-tooltip'
import { Spacer } from '@src/elements/spacer/spacer'
import { Divider } from '@src/elements/divider/divider'
import EventBus from '@src/lib/event_bus'

import './edit-kgis.sass'

type HealthGoalResponse = { health_goal: HealthGoal }

@Component({
  directives: {
    ClickOutside
  },
  computed: {
    activities () {
      return this.$store.state.activities || []
    },
    exerciseTags () {
      return this.$store.state.completeExerciseTags || []
    },
    goal () {
      return this.$store.state.goal
    }
  }
})
export default class EditKgis extends Vue {

  @Prop({ required: false })
  backCallback: () => void

  @Prop({ required: false })
  closeCallback: () => void

  protected currentGoal: HealthGoal = new HealthGoal()

  protected showAddKGI: boolean = false

  protected apiClient: ApiClient = new ApiClient()

  protected fieldErrors: string[] = []

  protected goal: HealthGoal

  protected activities: Activity[]
  protected exerciseTags: any

  protected kgisToRemove: number[] = []

  constructor () {
    super()
  }

  beforeMount () {
    // keep a copy of the initial list of indicators, to check the updates when saving
    this.setOriginalGoal(this.goal)

    // will be received from wizard component
    this.$on('save', this.save)
  }

  @Watch('goal')
  setOriginalGoal (newGoal) {
    let originalGoal = new HealthGoal(newGoal)
    originalGoal.indicators = newGoal ? newGoal.indicators.map(indicator => new HealthIndicator(indicator)) : []
    this.currentGoal = originalGoal
  }

  showHideAddKGI () {
    this.showAddKGI = !this.showAddKGI
  }

  back () {
    if (this.backCallback) {
      this.backCallback()
    }
  }

  close () {
    if (this.closeCallback) {
      this.closeCallback()
    }
  }

  render (h: CreateElement) {
    const kgis = this.buildKgisRows(h)

    return <Modal show={true} closeCallback={this.close} cssClass='edit-kgis' disableClickOutside={true}>
      <div slot='customTitle'>
        <div onClick={this.back} ref='backBtn'>
          <span class='custom-title'>{this.$t('PAGE.MEMBERS.GOALS.MEMBER_GOAL')}</span>
          <H6 class='goal-title'>{this.currentGoal.isTemplateGoal ? this.$t(`PAGE.MEMBERS.GOALS.${this.currentGoal.key}`) : this.currentGoal.name}</H6>
        </div>
      </div>

      <Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.KEY_HEALTH_GOAL_INDICATOR')}</Subtitle1>
      <Body2>{this.$t('PAGE.MEMBERS.GOALS.KGI_DESCRIPTION_1')}</Body2>
      <Spacer />

      <div class='add-kgi-section'>
        <Button type='remove' ref='addKgiBtn' class='add-kgi-btn' label={this.$t('PAGE.MEMBERS.GOALS.ADD_KGI')} click={this.showHideAddKGI} icon={'util/add'} />
      {this.showAddKGI ? <KGITooltip closeCallback={this.showHideAddKGI} /> : null}
      </div>

      <div class='kgis-wrapper'>{kgis}</div>

      <div class='btn-row' slot='footer'>
        <Button ref='cancelBtn' type='outline' label={this.$t('CANCEL')} click={this.close} />
        <Button ref='saveKgisBtn' label={this.$t('SAVE')} click={this.save} />
      </div>
    </Modal>
  }

  buildKgisRows (h: CreateElement) {
    let groupedIndicators = this.currentGoal.indicators.reduce((group, indicator) => {
      // initialize
      if (!this.kgisToRemove.includes(indicator.id)) {
        const groupName = indicator.type.replace('_duration', '').replace('_frequency', '')
        const sourceId = indicator.sourceId

        // will group based on the type and source id => group name = 'type:sourceId'
        if (!group[`${groupName}:${sourceId}`]) {
          group[`${groupName}:${sourceId}`] = []
        }
        // set values in the group
        group[`${groupName}:${sourceId}`].push(indicator)
      }
      return group
    }, {})

    return Object.keys(groupedIndicators).map(groupName => {
      return <div class='kgi-row'>
        <Divider />
        <Subtitle1 class='row'>
          {this.formatKgiHeader(groupedIndicators[groupName][0])}
          <Icon name='util/minus' click={() => this.markForDeletion(groupedIndicators[groupName])} ref='removeKGI' />
        </Subtitle1>
        <div class='kgi-form'>
          {this.renderKgiRow(groupedIndicators[groupName], h)}
        </div>
      </div>
    })
  }

  formatKgiHeader (kgi: HealthIndicator) {
    switch (kgi.type) {
      case HealthIndicatorType.ACTIVITY_FREQUENCY:
      case HealthIndicatorType.ACTIVITY_DURATION:
        const activity = this.activities.filter(activity => activity.id === kgi.sourceId)[0]
        return activity ? `${this.$t(`PAGE.MEMBERS.GOALS.KGI.ACTIVITY`)} - ${this.$t(`ACTIVITIES.${activity.key.toUpperCase()}`)}` : ''
      case HealthIndicatorType.EXERCISE_TAG_PERCENTAGE:
        const exerciseTag = this.exerciseTags.filter(tag => tag.id === kgi.sourceId)[0]
        const group = exerciseTag ? snakecaseKeys(exerciseTag.type.replace('ExerciseTags::', '')).toUpperCase() : ''
        return exerciseTag ? `${this.$t(`PAGE.MEMBERS.GOALS.KGI.${group}`)} - ${this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${exerciseTag.key.toUpperCase()}`)}` : ''
      case HealthIndicatorType.HOME_WORKOUT_FREQUENCY:
      case HealthIndicatorType.HOME_WORKOUT_DURATION:
        return this.$t('PAGE.MEMBERS.GOALS.KGI.HOME')
      case HealthIndicatorType.STATION_WORKOUT_FREQUENCY:
      case HealthIndicatorType.STATION_WORKOUT_DURATION:
        return this.$t('PAGE.MEMBERS.GOALS.KGI.STATION')
      default:
        return this.$t(`PAGE.MEMBERS.GOALS.KGI.${kgi.type.toUpperCase()}`)
    }
  }

  /* Each KGI has a different form format */
  renderKgiRow (kgis: HealthIndicator[], h: CreateElement) {
    switch (kgis[0].type) {
      case HealthIndicatorType.MET:
        return this.renderValueForm(kgis[0], h)
      case HealthIndicatorType.PIX_SCORE:
      case HealthIndicatorType.EXERCISE_TAG_PERCENTAGE:
        return this.renderPercentageForm(kgis[0], h)
      default:
        return this.renderDurationFrequencyForm(kgis, h)
    }
  }

  setIndicatorTarget (targetValue, kgi: HealthIndicator) {
    let foundIndicator = this.currentGoal.indicators.filter(indicator => indicator.id === kgi.id)[0]
    if (foundIndicator) {
      foundIndicator.target = targetValue
    } else {
      kgi.target = targetValue
      this.currentGoal.indicators.push(kgi)
    }
  }

  /* Input for value - MET */
  renderValueForm (kgi: HealthIndicator, h: CreateElement) {
    return <Input type='number' value={`${kgi.target}`} class={`${this.fieldErrors.includes('value') ? 'error' : ''}`}
                  valueChanged={value => this.setIndicatorTarget(value, kgi)} ref='valueInput' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.MET_WEEK')} />
  }

  /* Input for value - PIX score, exercise tag percentage */
  renderPercentageForm (kgi: HealthIndicator, h: CreateElement) {
    return <Input type='number' value={`${kgi.target}`} class={`${this.fieldErrors.includes('percentage') ? 'error' : ''}`}
                  valueChanged={value => this.setIndicatorTarget(value, kgi)} ref='scoreInput' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.PER_WEEK')} />
  }

  /* Inputs for duration and frequency - home/station workouts and activities */
  renderDurationFrequencyForm (kgis: HealthIndicator[], h: CreateElement) {
    let baseType = `${kgis[0].type}`
    baseType = baseType.replace('_duration', '').replace('_frequency', '')
    const durationKGI = kgis.filter(indicator => indicator.type.includes('duration'))[0]
      || new HealthIndicator({ type: `${baseType}_duration`, sourceId: kgis[0].sourceId, sourceType: kgis[0].sourceType, target: null })
    const frequencyKGI = kgis.filter(indicator => indicator.type.includes('frequency'))[0]
      || new HealthIndicator({ type: `${baseType}_frequency`, sourceId: kgis[0].sourceId, sourceType: kgis[0].sourceType, target: null })

    return <div class='row'>
      <Input type='number' value={`${durationKGI.target}`} valueChanged={value => this.setIndicatorTarget(value, durationKGI)} ref='durationInput' label={this.$t('MINUTES')} />
      <Input type='number' value={`${frequencyKGI.target}`} valueChanged={value => this.setIndicatorTarget(value, frequencyKGI)} ref='frequencyInput'
             label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.TIMES_WEEK')} />
    </div>
  }

  markForDeletion (kgisToDelete: HealthIndicator[]) {
    kgisToDelete.forEach(kgi => {
      const index = this.currentGoal.indicators.findIndex(item => (item.id === kgi.id) || (item === kgi))
      if (index >= 0) {
        let kgiId = this.currentGoal.indicators[index].id
        if (kgiId) {
          this.kgisToRemove.push(kgiId)
        }

        this.currentGoal.indicators.splice(index, 1)
      }

      this.$forceUpdate()
    })
  }

  save () {
    this.validate()
    if (this.fieldErrors.length === 0) {

      async.waterfall([
          // delete the removed KGIs; meaning KGIs which are in the original list, but not in the current (edited) list
        this.removeIndicators,
          // add or edit the remaining KGIs
        this.updateIndicators
      ],
        () => {
          this.close()
          this.loadCurrentGoal()
          this.$store.dispatch('showToast', { message: this.$t('BODY_MEASUREMENTS.SAVE_SUCCESS'), fadeOut: true })
          EventBus.trigger('reloadHealthGoal')
          EventBus.trigger('reloadSelectedMember')
        })
    }
  }

  protected validate () {
    let fieldErrors: string[] = []
    this.currentGoal.indicators.forEach(kgi => {
      fieldErrors.push(...validateHealthIndicator(kgi))
    })
    this.fieldErrors = fieldErrors
  }

  protected removeIndicators (done) {
    async.waterfall(
      // delete the removed KGIs
      this.kgisToRemove
        .map(id => {
          return done => this.removeIndicator(id, done)
        }),
      () => {
        this.kgisToRemove = []
        done(null)
      })
  }

  protected updateIndicators (done) {
    async.waterfall(
      this.currentGoal.indicators.map(indicator => {
        if (indicator.id) {
          return done => this.editIndicator(indicator, done)
        } else {
          return done => this.addIndicator(indicator, done)
        }
      }),
      () => done(null))
  }

  protected removeIndicator (id, done) {
    return this.apiClient.delete(`${API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id)}/${id}`)
      .finally(() => done(null))
  }

  protected addIndicator (indicator, done) {
    delete indicator.id
    delete indicator.value
    return this.apiClient.post(API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id), { indicator: snakecaseKeys(indicator) })
      .finally(() => done(null))
  }

  protected editIndicator (indicator, done) {
    const id = indicator.id

    // format the data to be sent to server
    delete indicator.id
    if (!indicator.sourceId || !indicator.sourceType) {
      delete indicator.sourceId
      delete indicator.sourceType
    }

    // if the new target is empty, remove the indicator
    if (indicator.target === '' || indicator.target === 0) {
      return this.removeIndicator(id, done)
    }
    return this.apiClient.put(`${API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id)}/${id}`, { indicator: snakecaseKeys(indicator) })
      .finally(() => done(null))
  }

  protected loadCurrentGoal () {
    setTimeout(() => {
      return this.apiClient.get(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
        .then((response: HealthGoalResponse) => {
          if (!this.goal) return
          let goal = new HealthGoal(camelcaseKeys(response.health_goal, { deep: true }))
          goal.indicators = response.health_goal.indicators.map(indicator => new HealthIndicator(camelcaseKeys(indicator, { deep: true })))
          this.$store.dispatch('setGoal', goal)
        })
        .catch(error => console.log('cannot load current health goal', error))
    }, 500)
  }
}
